/**
 * حساب كشف الحساب محلياً من Dexie عند Offline
 *
 * يقرأ من:
 *   - local_sales (فواتير البيع)           — للعميل (debit)
 *   - local_payments (دفعات العميل)         — للعميل (credit)
 *   - local_purchases (فواتير الشراء)       — للمورد (credit)
 *   - local_payments (دفعات المورد)         — للمورد (debit)
 *   - local_customers / local_suppliers     — معلومات الكيان
 *
 * يُعيد نفس شكل response السيرفر بالضبط:
 *   { entity_name, entity_phone, type, opening_balance, transactions[], total_debit, total_credit, balance }
 */
import { offlineDB } from './db';

function inDateRange(createdAt, from, to) {
  if (!createdAt) return true;
  const date = typeof createdAt === 'string' ? createdAt : String(createdAt);
  if (from && date < from) return false;
  if (to && date > to + 'T23:59:59') return false;
  return true;
}

function beforeDate(createdAt, from) {
  if (!from || !createdAt) return false;
  const date = typeof createdAt === 'string' ? createdAt : String(createdAt);
  return date < from;
}

function buildItemsDesc(items = [], fallback) {
  const top3 = items.slice(0, 3).map(it => `${it.name || it.product_name || ''} x${it.quantity || 1}`);
  let desc = top3.join(' + ');
  if (items.length > 3) desc += ` +${items.length - 3} منتجات أخرى`;
  return desc || fallback;
}

export async function computeAccountStatementOffline({ type, entity_id, date_from, date_to }) {
  let entity = null;
  const transactions = [];
  let total_debit = 0;
  let total_credit = 0;
  let opening_balance = 0;

  if (type === 'customer') {
    entity = await offlineDB.local_customers.get(entity_id);
    if (!entity) {
      // قد يكون المستخدم كتب اسم عميل غير موجود محلياً
      entity = { name: '', phone: '' };
    }

    const allSales = await offlineDB.local_sales.where('customer_id').equals(entity_id).toArray();
    const allPays = await offlineDB.local_payments.where('customer_id').equals(entity_id).toArray();

    // Opening balance: ما قبل date_from
    if (date_from) {
      for (const s of allSales) if (beforeDate(s.created_at, date_from)) opening_balance += (s.total || 0);
      for (const p of allPays) if (beforeDate(p.created_at, date_from)) opening_balance -= (p.amount || 0);
    }

    // Sales in period (debit)
    for (const s of allSales) {
      if (!inDateRange(s.created_at, date_from, date_to)) continue;
      if (date_from && beforeDate(s.created_at, date_from)) continue;
      const amount = s.total || 0;
      total_debit += amount;
      transactions.push({
        id: s.id,
        date: (s.created_at || '').slice(0, 10),
        type: 'بيع',
        description: buildItemsDesc(s.items || [], `فاتورة بيع #${s.invoice_number || ''}`),
        debit: amount,
        credit: 0,
        running_balance: 0,
        exchange_rate: s.exchange_rate || null,
        _offline_pending: s._offline_pending === 1 ? true : false,
      });
    }

    // Payments in period (credit)
    for (const p of allPays) {
      if (!inDateRange(p.created_at, date_from, date_to)) continue;
      if (date_from && beforeDate(p.created_at, date_from)) continue;
      const amount = p.amount || 0;
      total_credit += amount;
      transactions.push({
        id: p.id,
        date: (p.created_at || '').slice(0, 10),
        type: 'دفعة',
        description: p.notes || 'دفعة نقدية',
        debit: 0,
        credit: amount,
        running_balance: 0,
        exchange_rate: p.exchange_rate || null,
        _offline_pending: p._offline_pending === 1 ? true : false,
      });
    }
  } else {
    // supplier
    entity = await offlineDB.local_suppliers.get(entity_id);
    if (!entity) entity = { name: '', phone: '' };

    const allPurch = await offlineDB.local_purchases.where('supplier_id').equals(entity_id).toArray();
    const allPays = await offlineDB.local_payments.where('supplier_id').equals(entity_id).toArray();

    if (date_from) {
      for (const pu of allPurch) if (beforeDate(pu.created_at, date_from)) opening_balance -= (pu.total || 0);
      for (const p of allPays) if (beforeDate(p.created_at, date_from)) opening_balance += (p.amount || 0);
    }

    for (const pu of allPurch) {
      if (!inDateRange(pu.created_at, date_from, date_to)) continue;
      if (date_from && beforeDate(pu.created_at, date_from)) continue;
      const amount = pu.total || 0;
      total_credit += amount;
      transactions.push({
        id: pu.id,
        date: (pu.created_at || '').slice(0, 10),
        type: 'شراء',
        description: buildItemsDesc(pu.items || [], `فاتورة شراء #${pu.invoice_number || ''}`),
        debit: 0,
        credit: amount,
        running_balance: 0,
        exchange_rate: pu.exchange_rate || null,
        _offline_pending: pu._offline_pending === 1 ? true : false,
      });
    }

    for (const p of allPays) {
      if (!inDateRange(p.created_at, date_from, date_to)) continue;
      if (date_from && beforeDate(p.created_at, date_from)) continue;
      const amount = p.amount || 0;
      total_debit += amount;
      transactions.push({
        id: p.id,
        date: (p.created_at || '').slice(0, 10),
        type: 'صرف',
        description: p.notes || 'دفعة للمورد',
        debit: amount,
        credit: 0,
        running_balance: 0,
        exchange_rate: p.exchange_rate || null,
        _offline_pending: p._offline_pending === 1 ? true : false,
      });
    }
  }

  // فرز وحساب الرصيد المتداول
  transactions.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  let running = opening_balance;
  for (const tx of transactions) {
    running += (tx.debit || 0) - (tx.credit || 0);
    tx.running_balance = running;
  }

  return {
    entity_name: entity.name || '',
    entity_phone: entity.phone || '',
    type,
    opening_balance,
    transactions,
    total_debit,
    total_credit,
    balance: running,
    _offline: true,
  };
}

/** يستخرج query params من URL */
export function parseQueryParams(url) {
  try {
    const q = url.split('?')[1] || '';
    const params = {};
    for (const kv of q.split('&')) {
      if (!kv) continue;
      const [k, v = ''] = kv.split('=');
      params[decodeURIComponent(k)] = decodeURIComponent(v);
    }
    return params;
  } catch { return {}; }
}
